import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BellSimpleRinging, CheckCircle, XCircle, Info, Key, PaperPlaneRight, PenNib, Prohibit, Code } from "@phosphor-icons/react";
import { humanTime } from "@/lib/utils";
import { toast } from "sonner";

const EVENTS = [
  { key: "ticket_created", label: "Ticket created", desc: "Acknowledge the requester when a new ticket is logged." },
  { key: "ticket_assigned", label: "Ticket assigned", desc: "Notify the agent when a ticket is assigned to them." },
  { key: "ticket_reply", label: "New reply / public comment", desc: "Notify the requester when staff post an update." },
  { key: "ticket_resolved", label: "Ticket resolved", desc: "Notify the requester when their ticket is resolved." },
  { key: "ticket_status_changed", label: "Ticket status changed", desc: "Notify the requester on every status change (e.g. New → In Progress → On Hold)." },
];

const STATUS_STYLE = { sent: "text-emerald-700 dark:text-emerald-400", simulated: "text-sky-700 dark:text-sky-400", failed: "text-red-700 dark:text-red-400", skipped: "text-zinc-600 dark:text-zinc-400" };

export default function Notifications() {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [saving, setSaving] = useState(false);
  const [delivery, setDelivery] = useState(null);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [teamSig, setTeamSig] = useState("");
  const [savingSig, setSavingSig] = useState(false);
  const [excText, setExcText] = useState("");
  const [tmpl, setTmpl] = useState(null);
  const [tmplDefaults, setTmplDefaults] = useState({});
  const [tmplPlaceholders, setTmplPlaceholders] = useState([]);
  const [tmplEvent, setTmplEvent] = useState("ticket_created");
  const [savingTmpl, setSavingTmpl] = useState(false);

  const loadLog = () => api.get("/integrations/notifications/log").then(r => setLog(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/integrations/notifications").then(r => { setCfg(r.data); setExcText((r.data.exception_emails || []).join("\n")); });
    api.get("/integrations/email").then(r => setDelivery(r.data)).catch(() => {});
    api.get("/integrations/reply-signature").then(r => setTeamSig(r.data.team_signature || "")).catch(() => {});
    api.get("/integrations/email-templates").then(r => { setTmpl(r.data.events); setTmplDefaults(r.data.defaults || {}); setTmplPlaceholders(r.data.placeholders || []); }).catch(() => {});
    loadLog();
  }, []);

  const saveTmpl = async () => {
    setSavingTmpl(true);
    try {
      const { data } = await api.put("/integrations/email-templates", { events: tmpl });
      setTmpl(data.events);
      toast.success("Email templates saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingTmpl(false); }
  };
  const resetTmpl = () => setTmpl(t => ({ ...t, [tmplEvent]: tmplDefaults[tmplEvent] || "" }));

  // Client-side preview mirroring backend _render_template (placeholder fill + blank-line collapse).
  const renderPreview = (event, body) => {
    const sig = (teamSig || "").trim();
    const link = `${(delivery?.frontend_url || window.location.origin)}/incidents/abc123`;
    const status = event === "ticket_status_changed" ? "Open -> In Progress" : "Open";
    const ctx = {
      number: "INC-01234", title: "Cannot connect to VPN", priority: "P2",
      status, link, customer: "Alex Morgan", signature: sig,
      reply: event === "ticket_reply" ? "Thanks for the details — we've reset your VPN profile. Please try connecting again and let us know." : "",
    };
    let out = body || "";
    Object.entries(ctx).forEach(([k, v]) => { out = out.split(`{{${k}}}`).join(v ?? ""); });
    out = out.replace(/\n{3,}/g, "\n\n").trim();
    if (!(body || "").includes("{{signature}}") && sig && !out.includes(sig)) out = `${out}\n\n${sig}`;
    return out;
  };

  const saveTeamSig = async () => {
    setSavingSig(true);
    try {
      const { data } = await api.put("/integrations/reply-signature", { team_signature: teamSig });
      setTeamSig(data.team_signature || "");
      toast.success("Team signature saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingSig(false); }
  };

  if (!cfg) return <div className="p-6 text-muted-foreground">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const exception_emails = excText.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean);
      const { data } = await api.put("/integrations/notifications", { ...cfg, exception_emails });
      setCfg(data);
      setExcText((data.exception_emails || []).join("\n"));
      toast.success("Notification settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const saveDelivery = async () => {
    setSavingDelivery(true);
    try {
      const payload = { sender_email: delivery.sender_email, frontend_url: delivery.frontend_url };
      const { data } = await api.put("/integrations/email", payload);
      setDelivery(data);
      toast.success("Email delivery settings saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingDelivery(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integrations/email/test", { to: testTo.trim() });
      if (data.status === "sent") toast.success(data.detail);
      else if (data.status === "simulated") toast.info(data.detail);
      else toast.error(data.detail || "Test failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  return (
    <div className="p-6 max-w-4xl" data-testid="notifications-view">
      <div className="mb-6">
        <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-1">Integrations</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black flex items-center gap-3"><BellSimpleRinging size={30} className="text-primary" weight="fill" /> Email Notifications</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">Send lightweight email nudges to requesters and agents on standard ticket events. Delivery uses your <span className="text-primary">Microsoft 365 (Office 365 / Graph)</span> mailbox; when Graph isn't configured, events are logged in simulation mode.</p>
      </div>

      {delivery && (
        <Card className="p-6 border-primary/40 bg-primary/5 rounded-sm mb-6 space-y-5" data-testid="email-delivery-card">
          <div className="flex items-center gap-2">
            <Key size={16} className="text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Email Delivery · Microsoft 365 (Graph)</span>
            <span className={`ml-auto text-[10px] font-mono px-2 py-0.5 border rounded-sm ${delivery.configured ? 'border-emerald-500/50 text-emerald-700 dark:text-emerald-400' : 'border-amber-500/50 text-amber-700 dark:text-amber-400'}`} data-testid="delivery-status">
              {delivery.configured ? "CONFIGURED" : "NOT CONFIGURED"}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">Outbound email is sent through your Office 365 mailbox via Microsoft Graph (using the app's Entra credentials). Set the approved sender mailbox and app URL below, then send a test. No API key required.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">Sender mailbox (from)</Label>
              <Input data-testid="delivery-sender" className="mt-1 rounded-sm font-mono text-xs" placeholder="helpdesk@yourcompany.com" value={delivery.sender_email || ""} onChange={(e) => setDelivery({ ...delivery, sender_email: e.target.value })} />
            </div>
            <div className="md:col-span-2">
              <Label className="text-[10px] font-mono uppercase text-muted-foreground">App URL (for ticket links in emails)</Label>
              <Input data-testid="delivery-frontend-url" className="mt-1 rounded-sm font-mono text-xs" placeholder="https://your-trak-domain.com" value={delivery.frontend_url || ""} onChange={(e) => setDelivery({ ...delivery, frontend_url: e.target.value })} />
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Button data-testid="delivery-save" onClick={saveDelivery} disabled={savingDelivery} className="rounded-sm font-mono uppercase text-xs">{savingDelivery ? "Saving…" : "Save Settings"}</Button>
            <div className="flex items-end gap-2">
              <div>
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Send test to</Label>
                <Input data-testid="delivery-test-to" className="mt-1 rounded-sm font-mono text-xs w-56" placeholder="your@email.com (defaults to you)" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
              </div>
              <Button data-testid="delivery-test" onClick={sendTest} disabled={testing} variant="outline" className="rounded-sm font-mono uppercase text-xs border-primary/40 h-9"><PaperPlaneRight size={13} className="mr-2" />{testing ? "Sending…" : "Send Test"}</Button>
            </div>
          </div>
        </Card>
      )}

      <Card className="p-6 border-border rounded-sm mb-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-lg">Enable email nudges</div>
            <div className="text-xs text-muted-foreground">Master switch for all standard email notifications.</div>
          </div>
          <Switch data-testid="notif-enabled" checked={cfg.enabled} onCheckedChange={(v)=>setCfg({...cfg, enabled: v})} />
        </div>

        <div>
          <Label className="text-xs uppercase text-muted-foreground">Notify on</Label>
          <div className="mt-2 grid grid-cols-1 gap-2">
            {EVENTS.map(e => (
              <div key={e.key} className="flex items-center justify-between border border-border rounded-sm px-3 py-2.5">
                <div>
                  <div className="text-sm">{e.label}</div>
                  <div className="text-[11px] text-muted-foreground">{e.desc}</div>
                </div>
                <Switch data-testid={`notif-event-${e.key}`} checked={cfg.events?.[e.key] ?? true} onCheckedChange={(v)=>setCfg({...cfg, events: {...cfg.events, [e.key]: v}})} />
              </div>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1"><Info size={12} /> Toggles have no effect while the master switch is off.</div>
        </div>

        <Button data-testid="notif-save" onClick={save} disabled={saving} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save Settings"}</Button>
      </Card>

      <Card className="p-6 border-primary/40 bg-primary/5 rounded-sm mb-6 space-y-4" data-testid="email-templates-card">
        <div className="flex items-center gap-2">
          <Code size={16} className="text-primary" weight="bold" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Email Templates</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Customise the body of each automated email. Pick an event, edit its message, and use placeholders (below) that get filled in per ticket. The subject line stays <span className="font-mono">[NUMBER] Title</span> so email replies keep threading to the right ticket.
        </p>
        {!tmpl ? <div className="text-sm text-muted-foreground">Loading…</div> : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Event</Label>
                <Select value={tmplEvent} onValueChange={setTmplEvent}>
                  <SelectTrigger data-testid="tmpl-event-select" className="mt-1 w-64 rounded-sm text-xs h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EVENTS.map(e => <SelectItem key={e.key} value={e.key}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-mono uppercase text-muted-foreground mr-1">Insert:</span>
              {tmplPlaceholders.map(p => (
                <button key={p} type="button" data-testid={`tmpl-ph-${p}`}
                  onClick={() => setTmpl(t => ({ ...t, [tmplEvent]: (t[tmplEvent] || "") + `{{${p}}}` }))}
                  className="text-[10px] font-mono px-2 py-0.5 border border-primary/40 text-primary rounded-sm hover:bg-primary/10 transition-colors">
                  {`{{${p}}}`}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-[10px] font-mono uppercase text-muted-foreground">Template body</Label>
                <Textarea
                  data-testid="tmpl-body-textarea"
                  value={tmpl[tmplEvent] || ""}
                  onChange={(e) => setTmpl({ ...tmpl, [tmplEvent]: e.target.value })}
                  className="rounded-sm min-h-[260px] font-mono text-xs leading-relaxed"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-mono uppercase text-muted-foreground flex items-center gap-1.5"><PaperPlaneRight size={11} /> Live preview (sample data)</Label>
                <div data-testid="tmpl-preview" className="rounded-sm border border-border bg-background/60 min-h-[260px] overflow-hidden">
                  <div className="border-b border-border px-3 py-2 bg-muted/40">
                    <div className="text-[10px] font-mono text-muted-foreground">SUBJECT</div>
                    <div className="text-xs font-mono text-foreground truncate" data-testid="tmpl-preview-subject">[INC-01234] Cannot connect to VPN</div>
                  </div>
                  <pre className="px-3 py-2.5 text-xs leading-relaxed whitespace-pre-wrap font-sans text-foreground" data-testid="tmpl-preview-body">{renderPreview(tmplEvent, tmpl[tmplEvent] || "")}</pre>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button data-testid="tmpl-save" onClick={saveTmpl} disabled={savingTmpl} className="rounded-sm font-mono uppercase text-xs">{savingTmpl ? "Saving…" : "Save Templates"}</Button>
              <Button data-testid="tmpl-reset" onClick={resetTmpl} variant="outline" className="rounded-sm font-mono uppercase text-xs border-primary/40">Reset to default</Button>
            </div>
          </>
        )}
      </Card>

      <Card className="p-6 border-amber-500/40 bg-amber-500/5 rounded-sm mb-6 space-y-4" data-testid="email-exception-card">
        <div className="flex items-center gap-2">
          <Prohibit size={16} className="text-amber-600 dark:text-amber-400" weight="bold" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-amber-700 dark:text-amber-400">Email Exception List</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Automated notifications and nudges (ticket created, assigned, resolved, status changed) will <b>never be sent</b> to the addresses below — use this to stop auto-responder loops and silence disabled or junk-tracking mailboxes. Manual agent replies still go through, but the <span className="text-primary">Send test</span> above is also blocked for these addresses. One address per line (commas also accepted).
        </p>
        <Textarea
          data-testid="exception-emails-textarea"
          value={excText}
          onChange={(e)=>setExcText(e.target.value)}
          placeholder={"noreply@vendor.com\nauto-responder@partner.com"}
          className="rounded-sm min-h-[100px] font-mono text-sm"
        />
        <div className="flex items-center gap-3">
          <Button data-testid="exception-save" onClick={save} disabled={saving} className="rounded-sm font-mono uppercase text-xs bg-amber-600 hover:bg-amber-700 text-white">{saving ? "Saving…" : "Save Exception List"}</Button>
          <span className="text-[11px] font-mono text-muted-foreground">{excText.split(/[\s,;]+/).map(s=>s.trim()).filter(Boolean).length} blocked</span>
        </div>
      </Card>

      <Card className="p-6 border-border rounded-sm mb-6 space-y-4" data-testid="team-signature-card">
        <div className="flex items-center gap-2">
          <PenNib size={16} className="text-primary" />
          <span className="text-[11px] font-mono uppercase tracking-widest text-primary">Team reply signature</span>
        </div>
        <p className="text-xs text-muted-foreground">
          Appended to the bottom of reply emails for agents who haven't set a personal signature (Sidebar → <span className="text-primary">pen icon</span> next to Sign out). Leave empty to use the default "— TRAK Service Desk".
        </p>
        <Textarea
          data-testid="team-signature-textarea"
          value={teamSig}
          onChange={(e)=>setTeamSig(e.target.value)}
          placeholder={"e.g.\nTRAK Service Desk\nsupport@yourcompany.com · +1 555 0100"}
          className="rounded-sm min-h-[110px] font-mono text-sm"
        />
        <Button data-testid="team-signature-save" onClick={saveTeamSig} disabled={savingSig} className="rounded-sm font-mono uppercase text-xs">{savingSig ? "Saving…" : "Save Team Signature"}</Button>
      </Card>

      <Card className="border-border rounded-sm overflow-hidden" data-testid="notif-log-card">
        <div className="p-4 border-b border-border"><div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Delivery Log</div></div>
        <div className="divide-y divide-border/60">
          {log.length === 0 && <div className="p-4 text-sm text-muted-foreground">No emails sent yet.</div>}
          {log.map(l => (
            <div key={l.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
              {l.status === "sent" || l.status === "simulated" ? <CheckCircle size={14} className={STATUS_STYLE[l.status]} /> : <XCircle size={14} className={STATUS_STYLE[l.status] || "text-zinc-600 dark:text-zinc-400"} />}
              <span className="font-mono text-xs uppercase text-muted-foreground w-36">{l.event_type}</span>
              <span className="text-xs text-muted-foreground w-48 truncate">{l.to}</span>
              <span className={`text-xs font-mono ${STATUS_STYLE[l.status] || ""}`}>{l.status}</span>
              <span className="text-xs text-muted-foreground truncate flex-1">{l.detail}</span>
              <span className="text-[10px] font-mono text-muted-foreground">{humanTime(l.created_at)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
